import React, { useState, useRef, useEffect } from 'react';
import propTypes from 'prop-types';
import { TextContent } from '_ui-core-js/React/components/TextContent.jsx';
import { Button } from '_ui-core-js/React/components/Button.jsx';
import { CssAnimation } from '_ui-core-js/React/components/CssAnimation.jsx';
import { ColorPicker } from '_ui-core-js/React/components/ColorPicker.jsx';
import { Picture } from '_ui-core-js/React/components/Picture.jsx';
import { multiplier } from '_ui-core-js/utilities/multiplier.js';
import { VIEWS } from './AiExperience.jsx';
import { fireGTMTrackingEvent } from '_ui-core-js/utilities//fireGTMTrackingEvent.js';
import { CORE_GTM_TRACKING_VALUES } from '_ui-core-js/constants/analytics.js';
import { LBX_GTM_TRACKING_VALUES } from '../../constants/analytics.js';

export function CarColorSelectionScreen(props) {

  const { block, data, state } = props;
  const { title, subtitle, copy, ctas, colorPickerLabel, carColors} = data;
  const [activeColor, setActiveColor] = useState(carColors[0]);
  const isMountedRef = useRef();

  function updateCarColorSelection(activeColor) {
    setActiveColor(activeColor);
  };

  function proceedToArtworkGeneration() {
      fireGTMTrackingEvent({
        name: CORE_GTM_TRACKING_VALUES.name.ctaEvent,
        componentName: LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceAiExperience, 
        componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
        action: CORE_GTM_TRACKING_VALUES.action.clickCta,
        label: CORE_GTM_TRACKING_VALUES.label.createArtwork
      });

    state.setSelectedCarColorCode(activeColor.code);
    state.setSelectedCarColorLabel(activeColor.label);
    state.setActiveView(VIEWS.artworkGeneration.name);
  };

  useEffect(() => {
    fireGTMTrackingEvent({
      name: CORE_GTM_TRACKING_VALUES.name.workflowEvent,
      componentName: LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceAiExperience, 
      componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
      action: CORE_GTM_TRACKING_VALUES.action.workflowStep,
      label: CORE_GTM_TRACKING_VALUES.label.carColourScreen
    }, {
      workflow: {
        name: LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceAiExperience,
        step: CORE_GTM_TRACKING_VALUES.label.carColourScreen,
        conversion: 0,
        variant: (state.loggedIn && state.instagramCode) ? 'instagram' : 'not-connected'
      }
    });

    isMountedRef.current = true;
  }, []);

  const colorPaletteAndCarImage = () => (
    <CssAnimation 
    enter={{ name: 'slide-up-fade-in', duration: 1, easing: 'ease-out', delay: '0.5, hidden' }} 
    triggerEnter={true} 
    disable={isMountedRef.current}
    >
      <div className={`${block}__car-color-vehicle-image-container`}>
        <ul className={`${block}__color-palette`}>
          {
            state.colorPalettePayload?.map((value, index) => (
              <CssAnimation 
              enter={{name: 'vertical-top-bottom-clip', duration: 0.75, easing: 'ease-out', delay: `${multiplier(index, 0.20, 0.75)}, hidden`}}
              triggerEnter={true}
              disable={isMountedRef.current}
              key={value}
              >
                <li 
                className={`${block}__color-palette-item`} 
                style={{background: value}}>
                </li>
              </CssAnimation>
            ))
          }
        </ul>
        <Picture base={_.find(activeColor.images, { view: 'front' }).image} />
      </div>
      <div className={`${block}__car-color-vehicle-details-container`}>
        <CssAnimation 
        enter={{ name: 'slide-up-fade-in', duration: 0.75, easing: 'ease-out', delay: '1.5, hidden' }} 
        triggerEnter={true} 
        disable={isMountedRef.current}
        >
          <div className={`${block}__car-color-vehicle-model h3`}>{state.model} {state.atmosphere.toUpperCase()}</div>
        </CssAnimation>
        <CssAnimation 
        enter={{ name: 'slide-up-fade-in', duration: 0.75, easing: 'ease-out', delay: '1.75, hidden' }} 
        triggerEnter={true} 
        retrigger={[activeColor.label, {delay: 0}]}
        disable={isMountedRef.current}
        >
          <div className={`${block}__car-color-name list-item`}>{activeColor.label}</div>
        </CssAnimation>
      </div>
    </CssAnimation>
  );
  
  return (
    <div className={`${block}__car-color-screen ${block}__layout--horizontal container-xxxl`}>
      <div className='row'>
        <div className={`${block}__layout--block-one col-12 col-md-5`}>
          <TextContent 
          heading={title}
          subheading={subtitle}
          body={copy}
          modifier='centered-mobile'
 		  cssAnimationTrigger={true}
          />
          { 
            state.smallView && 
              colorPaletteAndCarImage() 
          }
          <CssAnimation
          enter={{ name: 'fade-in', duration: 1, delay: `1, hidden` }}
          triggerEnter={true}
          >
            <div className={`${block}__car-color-select-container`}>
              <div className='list-item'>{colorPickerLabel}</div>
              <ColorPicker
              colors={carColors}
              metallicColors={true}
              onChangeCallback={activeColor => updateCarColorSelection(activeColor)}
              />
            </div>
          </CssAnimation>
          <div className={`${block}__cta-container`}>
            <Button 
            label={ctas.next.label} 
            type='primary' 
            cssAnimation={true} 
            cssAnimationName='vertical-top-bottom-clip'
            cssAnimationTrigger={true}
            cssAnimationDuration={0.5}
            cssAnimationDelay={1} 
            onClickCallback={proceedToArtworkGeneration}
            />
            <Button 
            label={ctas.back.label}
            type='tertiary' 
            icon='back'
            slim={true}
            cssAnimation={true} 
            cssAnimationTrigger={true}
            cssAnimationDelay={1.5}
            onClickCallback={() => {
              fireGTMTrackingEvent({
                name: CORE_GTM_TRACKING_VALUES.name.componentEvent,
                componentName: LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceAiExperience, 
                componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
                action: CORE_GTM_TRACKING_VALUES.action.previousStep,
                label: CORE_GTM_TRACKING_VALUES.label.carColourScreen
              });
              
              state.setActiveView(VIEWS.userChoicesSelection.name)}
            }
            />
          </div>
        </div>
        <div className={`${block}__layout--block-two col-12 col-md-6 offset-md-1`}>
          { 
            !state.smallView && 
              colorPaletteAndCarImage() 
          }
        </div>
      </div>
    </div>
  )
};

CarColorSelectionScreen.propTypes = {
  block: propTypes.string.isRequired,
  state: propTypes.object.isRequired,
  data: propTypes.object.isRequired
};

